import { DataLoadError, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import { Navigate, NavLink, Outlet, useMatch, useOutletContext, useParams } from 'react-router-dom';
import { COMPANY_ROUTES, ROUTES } from '../../../Base.constants';
import { useDocumentTitle } from '../../../hooks/common';
import useFetchCompanyDocuments from '../../../hooks/documents/useFetchCompanyDocuments';
import { CompanyPageParams } from '../../../models/CompanyPageParams';
import { CompanyOutletContext } from '../Company';
import styles from './CompanyDocumentsTab.module.scss';

const cx = classNames.bind(styles);

const CompanyDocumentsTab = (): React.ReactElement => {
  const { issuerId } = useParams() as CompanyPageParams;
  const { company } = useOutletContext<CompanyOutletContext>();
  const documentTitle = company ? `${company.displayName}: Documents` : '';
  useDocumentTitle(documentTitle);

  const isDocumentsRoute = useMatch(`${ROUTES.company(issuerId)}/${COMPANY_ROUTES.documents}`);
  const isFilingsRoute = useMatch(
    `${ROUTES.company(issuerId)}/${COMPANY_ROUTES.documents}/${COMPANY_ROUTES.filingsDocuments}`
  );
  const { isLoading, isError, refetch, bondsTotalResults, loansTotalResults, filingsTotalResults } =
    useFetchCompanyDocuments(issuerId);

  if (isLoading) {
    return (
      <div className={cx('container')}>
        <Loader position="center" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cx('container')}>
        <DataLoadError onRetryClick={refetch} />
      </div>
    );
  }

  if (!bondsTotalResults && !loansTotalResults && !filingsTotalResults) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (!filingsTotalResults && isFilingsRoute) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (isDocumentsRoute) {
    let to = COMPANY_ROUTES.bondsDocuments;

    if (!bondsTotalResults) {
      to = loansTotalResults ? COMPANY_ROUTES.loansDocuments : COMPANY_ROUTES.filingsDocuments;
    }
    return <Navigate to={to} replace />;
  }

  return (
    <section className={cx('body')} data-testid="company-documents-tab">
      <div className={cx('categories')}>
        {bondsTotalResults || loansTotalResults ? (
          <>
            <NavLink
              data-testid="bond-reports"
              to={COMPANY_ROUTES.bondsDocuments}
              className={({ isActive }) => cx('category-link', { active: isActive })}
            >
              Bonds ({bondsTotalResults})
            </NavLink>
            <NavLink
              data-testid="loan-reports"
              to={COMPANY_ROUTES.loansDocuments}
              className={({ isActive }) => cx('category-link', { active: isActive })}
            >
              Loans ({loansTotalResults})
            </NavLink>
          </>
        ) : null}
        {filingsTotalResults ? (
          <NavLink
            data-testid="common-reports"
            to={COMPANY_ROUTES.filingsDocuments}
            className={({ isActive }) => cx('category-link', { active: isActive })}
          >
            Documents/Filings ({filingsTotalResults})
          </NavLink>
        ) : null}
      </div>
      <Outlet />
    </section>
  );
};

export default CompanyDocumentsTab;
